import { isBoolean, isNullOrEmpty, isNumeric } from "../../../helpers/ObjectHelpers";

export interface IMapParamsService {
    showMap: boolean | null,
    showMapTerrain: boolean | null,
    showSatellite: boolean | null,
    showSatelliteLabels: boolean | null,
    showLegend: boolean | null,
    showLocations: boolean | null,
    showSeamap: boolean | null,
    mmsi: number | null,
    searchText: string | null,
    zoom: number | null,
    lat: number | null,
    lng: number | null,
    locationId: number | null,

    onShowMapChange(show:boolean): void,
    onShowMapTerrainChange(show: boolean): void,
    onShowSatelliteChange(show: boolean): void,
    onShowSatelliteLabelsChange(show: boolean): void,
    onShowLegendChange(show: boolean): void,
    onShowLocationsChange(show: boolean): void,
    onShowSeamapChange(show: boolean): void,
    onMmsiChange(mmsi: number): void,
    onSearchTextChange(text: string): void
    onZoomChange(zoom: number): void,
    onCenterChange(lat: number, lng: number): void,
    onLocationIdChange(locationId: number): void,
}

class MapParamsService implements IMapParamsService {

    _isActive = false;

    _searchParams: any;
    _setSearchParams: any;

    _showMap: boolean | null = null;
    _showMapTerrain: boolean | null = null;
    _showSatellite: boolean | null = null;
    _showSateliteLabels: boolean | null = null;
    _showLegend: boolean | null = null;
    _showLocations: boolean | null = null;
    _showSeamap: boolean | null = null;
    _mmsi: number | null = null;
    _searchText: string | null = null;
    _zoom: number | null = null;
    _lat: number | null = null;
    _lng: number | null = null;
    _locationId: number | null = null;

    constructor(
        isActive: boolean,
        searchParams: any,
        setSearchParams: any
    ) {
        this._isActive = isActive;
        
        this._searchParams = searchParams;
        this._setSearchParams = setSearchParams;

        this.init();
    }

    get showMap() { return this._showMap; }
    get showMapTerrain() { return this._showMapTerrain; }
    get showSatellite() { return this._showSatellite; }
    get showSatelliteLabels() { return this._showSateliteLabels; }
    get showLegend() { return this._showLegend; }
    get showLocations() { return this._showLocations; }
    get showSeamap() { return this._showSeamap; }
    get mmsi() { return this._mmsi; }
    get searchText() { return this._searchText }
    get zoom() { return this._zoom; }
    get lat() { return this._lat; }
    get lng() { return this._lng; }
    get locationId() { return this._locationId; }

    init() {

        const showMap = this._searchParams.get("smap");
        if (!isNullOrEmpty(showMap))
            this._showMap = showMap.toLowerCase() === "true" ? true : false;

        const showMapTerrain = this._searchParams.get("smapt");
        if (!isNullOrEmpty(showMapTerrain))
            this._showMapTerrain = showMapTerrain.toLowerCase() === "true" ? true : false;

        const showSatellite = this._searchParams.get("ssat");
        if (!isNullOrEmpty(showSatellite))
            this._showSatellite = showSatellite.toLowerCase() === "true" ? true : false;

        const showSateliteLabels = this._searchParams.get("ssatl");
        if (!isNullOrEmpty(showSateliteLabels))
            this._showSateliteLabels = showSateliteLabels.toLowerCase() === "true" ? true : false;

        const showLegend = this._searchParams.get("sleg");
        if (!isNullOrEmpty(showLegend))
            this._showLegend = showLegend.toLowerCase() === "true" ? true : false;

        const showLocations = this._searchParams.get("sloc");
        if (!isNullOrEmpty(showLocations))
            this._showLocations = showLocations.toLowerCase() === "true" ? true : false;

        const showSeamap = this._searchParams.get("sseamap");
        if (!isNullOrEmpty(showSeamap))
            this._showSeamap = showSeamap.toLowerCase() === "true" ? true : false;

        const mmsi = this._searchParams.get("mmsi");
        this._mmsi = isNumeric(mmsi)  ? Number(mmsi) : 0;

        const searchText = this._searchParams.get("searchText");
        if (!isNullOrEmpty(searchText))
            this._searchText = searchText;

        const zoom = this._searchParams.get("zoom");
        const lat = this._searchParams.get("lat");
        const lng = this._searchParams.get("lng");
        
        if (isNumeric(zoom) && isNumeric(lat) && isNumeric(lng)) {
            this._zoom = Number(zoom);
            this._lat = Number(lat);
            this._lng = Number(lng);
        } else {
            this._zoom = 0;
            this._lat = 0;
            this._lng = 0;
        }

        const locationId = this._searchParams.get("locationId");
        this._locationId = isNumeric(locationId) ? Number(locationId) : null;
    }

    onUpdateSearchParams() {
        if (!this._isActive) return;
        
        const params: any = {};

        if (isBoolean(this._showMap))
            params.smap = `${this._showMap}`;

        if (isBoolean(this._showMapTerrain))
            params.smapt = `${this._showMapTerrain}`;

        if (isBoolean(this._showSatellite))
            params.ssat = `${this._showSatellite}`;

        if (isBoolean(this._showSateliteLabels))
            params.ssatl = `${this._showSateliteLabels}`;

        if (isBoolean(this._showLegend)) 
            params.sleg = `${this._showLegend}`;

        if (isBoolean(this._showLocations))
            params.sloc = `${this._showLocations}`;

        if (isBoolean(this._showSeamap))
            params.sseamap = `${this._showSeamap}`;

        if (this._mmsi && this._mmsi > 0)
            params.mmsi = `${this._mmsi}`;

        if (!isNullOrEmpty(this._searchText))
            params.searchText = `${this._searchText}`;
        
        if (
            (this._zoom && this._zoom !== 0) &&
            (this._lat && this._lat !== 0) &&
            (this._lng && this._lng !== 0)
        ) {
            params.zoom = `${this._zoom}`;
            params.lat = `${this._lat}`;
            params.lng = `${this._lng}`;
        }

        if (this._locationId && this._locationId > 0)
            params.locationId = `${this._locationId}`;
        
        this._setSearchParams(params, { replace: true });
    }

    onShowMapChange(show: boolean) {
        this._showMap = show;
        this._showSatellite = !show;
        this.onUpdateSearchParams();
    }

    onShowMapTerrainChange(show: boolean) {
        this._showMapTerrain = show;
        this.onUpdateSearchParams();
    }

    onShowSatelliteChange(show: boolean) {
        this._showSatellite = show;
        this._showMap = !show;
        this.onUpdateSearchParams();
    }

    onShowSatelliteLabelsChange(show: boolean) {
        this._showSateliteLabels = show;
        this.onUpdateSearchParams();
    }

    onShowLegendChange(show: boolean) {
        this._showLegend = show;
        this.onUpdateSearchParams();
    }
    
    onShowLocationsChange(show: boolean) {
        this._showLocations = show;
        this.onUpdateSearchParams();
    }

    onShowSeamapChange(show: boolean) {
        this._showSeamap = show;
        this.onUpdateSearchParams();
    }

    onMmsiChange(mmsi: number) {
        this._mmsi = mmsi;
        this.onUpdateSearchParams();
    }

    onSearchTextChange(text: string) {
        this._searchText = text;
        this.onUpdateSearchParams();
    }

    onZoomChange(zoom: number) {
        this._zoom = zoom;
        this.onUpdateSearchParams();
    }

    onCenterChange(lat: number, lng: number) {
        this._lat = lat;
        this._lng = lng;
        this.onUpdateSearchParams();
    }

    onLocationIdChange(locationId: number) {
        this._locationId = locationId;
        this.onUpdateSearchParams();
    }
}

export default MapParamsService;
